<script lang="ts" setup>
import { api } from '@/api';
import {
  CreateOtherQaEquipmentDto,
  CreateQaEquipmentRtSystemDto,
  type QaEquipmentRtSystemDto,
} from '@/api/generator';
import Dialog from '@/components/Dialog/Dialog.vue';
import FormLabel from '@/components/FormLabel/FormLabel.vue';
import VeeFileInput from '@/components/forms/FileInput/VeeFileInput.vue';
import VeeSubmitButton from '@/components/forms/SubmitButton/VeeSubmitButton.vue';
import TextField from '@/components/forms/TextField/TextField.vue';
import { useVeeForm } from '@/components/forms/useVeeForm.ts';
import IdentifierLabel from '@/components/IdentifierLabel/IdentifierLabel.vue';
import InlineNotification from '@/components/notifications/InlineNotification.vue';
import Notification from '@/components/notifications/Notification.vue';
import { NotificationMessage } from '@/components/notifications/types.ts';
import { formBackendErrorHandler, stopErrorPropagation } from '@/errorHandling';
import { useStoreErrorNotification } from '@/errorHandling/useStoreErrorNotification.ts';
import { useNotificationStore } from '@/stores/notificationStore.ts';
import { I18nKey } from '@/utils/typings.ts';
import { reloadEquipmentKey } from '@/views/qaEquipment/types.ts';
import {
  getModelLabel,
  getTypeLabel,
  isRegistrablePtwQaEquipment,
} from '@/views/qaEquipment/utils';
import { useTranslation } from 'i18next-vue';
import { inject, ref, watch } from 'vue';

const modelValue = defineModel<boolean>({ type: Boolean, default: false });

type QaEquipmentUploadCalibrationFileDialogProps = {
  qaEquipment: Partial<
    QaEquipmentRtSystemDto | CreateQaEquipmentRtSystemDto | CreateOtherQaEquipmentDto
  >;
};
const props = defineProps<QaEquipmentUploadCalibrationFileDialogProps>();

const { t } = useTranslation();
const { success: notifySuccess } = useNotificationStore();
const reloadEquipment = inject(reloadEquipmentKey, undefined);

type UploadCalibrationFileFormValues = {
  calibrationFile: File | undefined;
};
const { form } = useVeeForm<UploadCalibrationFileFormValues>({
  mode: 'edit',
  options: {
    initialValues: {
      calibrationFile: undefined,
    },
  },
});
const { handleSubmit, resetForm, setFieldError } = form;

const storeNotification = ref<NotificationMessage | undefined>(undefined);
const { errorHandler, clearError } = useStoreErrorNotification({
  showNotificationInToast: true,
  notificationMessage: storeNotification,
  closable: false,
});

const submit = handleSubmit(async (data) => {
  if (!data.calibrationFile) {
    setFieldError('calibrationFile', t('Validation.PropertyRequired'));
    return;
  }
  if (!data.calibrationFile.name.toLowerCase().endsWith('.cal')) {
    setFieldError('calibrationFile', t('OnlyCalFileTypeAllowedMsg'));
    return;
  }
  if (!isRegistrablePtwQaEquipment(props.qaEquipment) || !props.qaEquipment.id) {
    return;
  }

  clearError();
  const { success } = await api.qaEquipmentApi.uploadCalibrationData(
    {
      id: props.qaEquipment.id,
      file: data.calibrationFile,
    },
    [formBackendErrorHandler(form), errorHandler, stopErrorPropagation],
  );
  if (success) {
    reloadEquipment?.();
    closeDialog();
    notifySuccess(t('UploadCalibrationFileSuccessMsg'));
  }
});

function closeDialog() {
  modelValue.value = false;
  resetForm();
}

watch(modelValue, (valueNew) => {
  if (valueNew) {
    clearError();
  }
});
</script>

<template>
  <Dialog
    v-if="isRegistrablePtwQaEquipment(qaEquipment)"
    v-model="modelValue"
    aria-label="UploadCalibrationFileDialog"
    hideClose
    size="medium"
  >
    <template #title>{{ $t('UploadAndActivateCalibrationFile') }}</template>

    <template #text>
      <Notification class="mb-4" :value="storeNotification" />
      <FormLabel :label="`${$t('Name')} | ${$t('ID')}`">
        <IdentifierLabel :label="qaEquipment.name" :identifier="qaEquipment.iuid" />
      </FormLabel>
      <v-divider class="my-4" />

      <v-row>
        <v-col cols="6">
          <TextField
            :label="$t('Type_one')"
            :modelValue="$t(getTypeLabel(qaEquipment) as I18nKey)"
            :mode="'details'"
          />
        </v-col>
        <v-col cols="6">
          <TextField
            :label="$t('Model')"
            :modelValue="getModelLabel(qaEquipment)"
            :mode="'details'"
          />
        </v-col>
        <v-col cols="6">
          <TextField
            :label="$t('ArticleNumber')"
            :modelValue="`${qaEquipment.typeNumber}`"
            :mode="'details'"
          />
        </v-col>
        <v-col cols="6">
          <TextField
            :label="$t('SerialNumber')"
            :modelValue="qaEquipment.serialNumber"
            :mode="'details'"
          />
        </v-col>
      </v-row>

      <p class="text-body-1 mt-6 mb-2">
        {{ $t('UploadCalibrationCertificateFromLocalSystemMsg') }}
      </p>
      <VeeFileInput
        name="calibrationFile"
        mode="edit"
        :label="$t('CalibrationFile')"
        accept=".cal"
        :placeholder="$t('Required')"
      />

      <InlineNotification
        class="w-100 mt-4"
        data-testid="InlineNotification_VerifyCorrectCalibrationFile"
        :value="{
          type: 'warning',
          title: $t('VerifyCorrectCalibrationFileTitle'),
          message: $t('VerifyCorrectCalibrationFileMsg'),
        }"
      />
    </template>
    <template #actions>
      <TextVBtn @click="closeDialog">
        {{ $t('Cancel') }}
      </TextVBtn>
      <VeeSubmitButton style="margin-left: 16px" :text="$t('ActivateNow')" @click="submit" />
    </template>
  </Dialog>
</template>
